/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/execution/commit/extrude 层；
 * 2. 负责执行拉伸凸台的正式提交；
 * 3. 该类把“新建提交”和“编辑替换提交”统一收口到 cad-core。
 */

import type { Viewer } from '../../../../viewer';
import { FeatureCommitExecution } from '../FeatureCommitExecution';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ExtrudeCommandAdapter } from '../../../command/extrude/ExtrudeCommandAdapter';
import type { FeatureExecutionRuntime } from '../../FeatureExecutionRuntime';
import type { ExtrudeFeatureDefinitionModel } from '../../../definition/extrude/ExtrudeFeatureDefinition';
import type { ExtrudeFeaturePrototype } from '../../../prototype/extrude/ExtrudeFeaturePrototype';

/**
 * 拉伸特征正式提交执行器。
 */
export class ExtrudeCommitExecution extends FeatureCommitExecution<
    ExtrudeFeatureDefinitionModel,
    ExtrudeFeaturePrototype
> {
    /** 特征执行运行时。 */
    private readonly runtime: FeatureExecutionRuntime;
    /** 特征替换提交通道。 */
    private readonly replaceFeatureCommand: (featureId: string, command: CadCommand) => Promise<boolean>;
    /** 拉伸命令适配器。 */
    private readonly adapter: ExtrudeCommandAdapter;

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(
        _viewer: Viewer,
        runtime: FeatureExecutionRuntime,
        replaceFeatureCommand: (featureId: string, command: CadCommand) => Promise<boolean>,
    ) {
        super();
        this.runtime = runtime;
        this.replaceFeatureCommand = replaceFeatureCommand;
        this.adapter = new ExtrudeCommandAdapter();
    }

    /**
     * @inheritdoc
     */
    public async commitCreate(prototype: ExtrudeFeaturePrototype): Promise<string[]> {
        const command = this.adapter.createCommandFromPrototype({
            prototype,
            clientId: 'web-client',
        }) as CadCommand;
        const result = await this.runtime.executeCommand(command);
        return Array.isArray(result.objectIds) && result.objectIds.length > 0
            ? result.objectIds
            : [result.objectId];
    }

    /**
     * @inheritdoc
     */
    public async commitEdit(
        session: FeatureEditSession<ExtrudeFeatureDefinitionModel, ExtrudeFeaturePrototype>,
        prototype: ExtrudeFeaturePrototype,
    ): Promise<boolean> {
        const command = this.adapter.createCommandFromPrototype({
            prototype,
            clientId: 'web-client',
        }) as CadCommand;
        return this.replaceFeatureCommand(session.getPrototype().getFeatureId(), command);
    }
}
